import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Button, Dialog, DialogActions,
  DialogContent, DialogTitle, TextField, IconButton,
  LinearProgress,
  MenuItem,
} from '@mui/material';
import { Edit, Delete } from '@mui/icons-material';
import axios from 'axios';
import api from '../../../api/axiosInterceptor';
import { API_BASE_URL } from '../../../config';
import TableComponent from '../../../Components/TableComponent/TableComponent';
import { LoadingSpinner } from '../../../Components/spinner/spinner';
import { SnackBarComponent } from '../../../Components/snackBar/snackBar';

// Fetch FE Base list for dropdown
const useFetchFeBaseList = () => {
  const [list, setList] = useState([]);
  useEffect(() => {
    axios.get(`${API_BASE_URL}/fe-mgmt/fe-base/`)
      .then(res => setList(res.data))
      .catch(() => setList([]));
  }, []);
  return list;
};

export const AdminFeCourtCasesMasterComponent = () => {
  const feBaseList = useFetchFeBaseList();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('info');

  const [openFormDialog, setOpenFormDialog] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  // Form state: for each field, store value, error status, and error helper text
  const [formFields, setFormFields] = useState({
    financial_establishment: { value: '', error: false, helperText: '' },
    case_no: { value: '', error: false, helperText: '' },
    court_name: { value: '', error: false, helperText: '' },
    status: { value: '', error: false, helperText: '' },
    description: { value: '', error: false, helperText: '' },
    doc: { value: null, error: false, helperText: '' },
  });

  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);

  const columns = [
    { id: 'slNo', field: 'slNo', headerName: 'Sl No.', width: 70 },
    { id: 'financial_establishment', field: 'financial_establishment', headerName: 'FE Base', width: 180 },
    { id: 'case_no', field: 'case_no', headerName: 'Case No', width: 150 },
    { id: 'court_name', field: 'court_name', headerName: 'Court Name', width: 200 },
    { id: 'status', field: 'status', headerName: 'Status', width: 150 },
    { id: 'description', field: 'description', headerName: 'Description', width: 250 },
    {
      id: 'doc',
      field: 'doc',
      headerName: 'Document',
      width: 120,
      renderCell: (params) =>
        params.value ? (
          <a href={params.value} target="_blank" rel="noopener noreferrer">View</a>
        ) : (
          'No Doc'
        ),
    },
    {
      id: 'actions',
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      renderCell: (params) => (
        <>
          <IconButton color="primary" onClick={() => handleEdit(params.row)} disabled={uploading}>
            <Edit />
          </IconButton>
          <IconButton color="error" onClick={() => handleDeleteConfirmation(params.row)} disabled={uploading}>
            <Delete />
          </IconButton>
        </>
      ),
    },
  ];

  // Fetch list of court cases and map FE id to FE name
  const fetchCourtCases = () => {
    setLoading(true);
    axios.get(`${API_BASE_URL}/fe-mgmt/fe-court-cases/`)
      .then(response => {
        const apiData = response.data.map((item, index) => {
          const feBaseObj = feBaseList.find(fe => fe.id === (item.financial_establishment?.id || item.financial_establishment));
          return {
            id: item.id,
            slNo: index + 1,
            financial_establishment: feBaseObj ? feBaseObj.name_of_fe : (item.financial_establishment?.name_of_fe || item.financial_establishment),
            case_no: item.case_no,
            court_name: item.court_name,
            status: item.status,
            description: item.description,
            doc: item.doc,
          };
        });
        setRows(apiData);
        setLoading(false);
      })
      .catch(error => {
        console.error('Failed to fetch court cases:', error);
        setSnackbarMessage('Failed to load court cases.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        setLoading(false);
      });
  };

  useEffect(() => {
    if (feBaseList.length > 0) {
      fetchCourtCases();
    }
  }, [feBaseList]);

  const handleSnackbarClose = () => setSnackbarOpen(false);

  const resetFormFields = () => {
    setFormFields({
      financial_establishment: { value: '', error: false, helperText: '' },
      case_no: { value: '', error: false, helperText: '' },
      court_name: { value: '', error: false, helperText: '' },
      status: { value: '', error: false, helperText: '' },
      description: { value: '', error: false, helperText: '' },
      doc: { value: null, error: false, helperText: '' },
    });
    setUploadProgress(0);
  };

  const handleAddItem = () => {
    setIsEditMode(false);
    resetFormFields();
    setOpenFormDialog(true);
  };

  // Set form fields values plus reset errors on edit; ensure FE dropdown selection uses FE Base id
  const handleEdit = (item) => {
    setIsEditMode(true);
    setSelectedItem(item);
    const feBaseObj = feBaseList.find(fe => fe.name_of_fe === item.financial_establishment || fe.id === item.financial_establishment);

    setFormFields({
      financial_establishment: { value: feBaseObj ? feBaseObj.id : '', error: false, helperText: '' },
      case_no: { value: item.case_no, error: false, helperText: '' },
      court_name: { value: item.court_name, error: false, helperText: '' },
      status: { value: item.status, error: false, helperText: '' },
      description: { value: item.description, error: false, helperText: '' },
      doc: { value: null, error: false, helperText: '' }, // doc empty on edit, new upload replaces
    });
    setUploadProgress(0);
    setOpenFormDialog(true);
  };

  const handleDeleteConfirmation = (item) => {
    setDeleteItem(item);
    setDeleteDialogOpen(true);
  };

  const handleDelete = () => {
    api.delete(`/fe-mgmt/fe-court-cases/${deleteItem.id}/`)
      .then(() => {
        setSnackbarMessage('Court Case deleted successfully.');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        fetchCourtCases();
      })
      .catch(error => {
        console.error('Delete failed:', error);
        setSnackbarMessage('Failed to delete Court Case.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      })
      .finally(() => {
        setDeleteDialogOpen(false);
        setDeleteItem(null);
      });
  };

  // Validate individual field and return error message or empty string
  const validateField = (name, value) => {
    if (name === 'financial_establishment' && !value) return 'FE Base is required';
    if (name === 'case_no' && !value.trim()) return 'Case number is required';
    if (name === 'court_name' && !value.trim()) return 'Court name is required';
    if (name === 'status' && !value.trim()) return 'Status is required';
    if (name === 'description' && !value.trim()) return 'Description is required';
    if (name === 'doc' && !isEditMode && !value) return 'Document upload is required';
    return '';
  };

  // Handle form input change with validation
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const errorMsg = validateField(name, value);
    setFormFields(prev => ({
      ...prev,
      [name]: { value, error: !!errorMsg, helperText: errorMsg },
    }));
  };

  // Handle file input change separately
  const handleFileChange = (e) => {
    if (e.target.files.length > 0) {
      const file = e.target.files[0];
      const errorMsg = validateField('doc', file);
      setFormFields(prev => ({
        ...prev,
        doc: { value: file, error: !!errorMsg, helperText: errorMsg },
      }));
    }
  };

  // Validate all fields before submission
  const validateForm = () => {
    let isValid = true;
    const newFields = { ...formFields };
    Object.keys(newFields).forEach((field) => {
      const errorMsg = validateField(field, newFields[field].value);
      if (errorMsg) isValid = false;
      newFields[field] = {
        ...newFields[field],
        error: !!errorMsg,
        helperText: errorMsg,
      };
    });
    setFormFields(newFields);
    return isValid;
  };

  // Submit form handling with multipart form data
  const handleFormSubmit = async () => {
    if (!validateForm()) return;

    const payload = new FormData();
    payload.append('financial_establishment', formFields.financial_establishment.value);
    payload.append('case_no', formFields.case_no.value);
    payload.append('court_name', formFields.court_name.value);
    payload.append('status', formFields.status.value);
    payload.append('description', formFields.description.value);
    if (formFields.doc.value) {
      payload.append('doc', formFields.doc.value);
    }

    try {
      setUploading(true);
      setUploadProgress(0);

      if (isEditMode) {
        await api.patch(`/fe-mgmt/fe-court-cases/${selectedItem.id}/`, payload, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: progressEvent => {
            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(percent);
          },
        });
        setSnackbarMessage('Court Case updated successfully.');
      } else {
        await api.post(`/fe-mgmt/fe-court-cases/`, payload, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: progressEvent => {
            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(percent);
          },
        });
        setSnackbarMessage('Court Case added successfully.');
      }
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      fetchCourtCases();
      setOpenFormDialog(false);
    } catch (error) {
      console.error('Submit failed:', error);
      setSnackbarMessage('Failed to submit Court Case.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  if (loading) return <LoadingSpinner size={30} />;

  return (
    <Box sx={{ width: '100%', mt:8, mb:1, borderRadius:2, boxShadow:'0 4px 12px rgba(0,0,0,0.1)', backgroundColor:'#fff' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" bgcolor="rgba(195,234,203,0.1)" p={2}>
        <Typography variant="h4" component="h1" fontWeight="bold">FE Court Cases</Typography>
        <Button variant="contained" color="primary" onClick={handleAddItem} disabled={uploading}>Add</Button>
      </Box>

      <TableComponent columns={columns} rows={rows} manageColumnStatus enableSort sx={{ '& .MuiDataGrid-columnHeaders': { backgroundColor:'#1976d2', color:'#fff', fontWeight:'bold' } }} />

      <Dialog open={openFormDialog} onClose={() => !uploading && setOpenFormDialog(false)}>
        <DialogTitle>{isEditMode ? 'Edit Court Case' : 'Add Court Case'}</DialogTitle>
        <DialogContent>
          <TextField select margin="dense" label="FE Base" fullWidth name="financial_establishment"
            value={formFields.financial_establishment.value} error={formFields.financial_establishment.error} helperText={formFields.financial_establishment.helperText}
            onChange={handleInputChange} disabled={uploading}>
            {feBaseList.map(fe => <MenuItem key={fe.id} value={fe.id}>{fe.name_of_fe || fe.id}</MenuItem>)}
          </TextField>
          <TextField margin="dense" label="Case Number" fullWidth name="case_no"
            value={formFields.case_no.value} error={formFields.case_no.error} helperText={formFields.case_no.helperText}
            onChange={handleInputChange} disabled={uploading} />
          <TextField margin="dense" label="Court Name" fullWidth name="court_name"
            value={formFields.court_name.value} error={formFields.court_name.error} helperText={formFields.court_name.helperText}
            onChange={handleInputChange} disabled={uploading} />
          <TextField margin="dense" label="Status" fullWidth name="status"
            value={formFields.status.value} error={formFields.status.error} helperText={formFields.status.helperText}
            onChange={handleInputChange} disabled={uploading} />
          <TextField margin="dense" label="Description" multiline rows={3} fullWidth name="description"
            value={formFields.description.value} error={formFields.description.error} helperText={formFields.description.helperText}
            onChange={handleInputChange} disabled={uploading} />
          <Button variant="outlined" component="label" sx={{ mt: 2 }}>
            {formFields.doc.value ? formFields.doc.value.name : 'Upload Document'}
            <input type="file" hidden onChange={handleFileChange} disabled={uploading} />
          </Button>
          {formFields.doc.error && <Typography color="error" variant="body2" sx={{ mt: 1 }}>{formFields.doc.helperText}</Typography>}
          {uploading && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress variant="determinate" value={uploadProgress} />
              <Typography variant="body2" sx={{ textAlign: 'center', mt: 1 }}>Uploading... {uploadProgress}%</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => !uploading && setOpenFormDialog(false)} disabled={uploading}>Cancel</Button>
          <Button onClick={handleFormSubmit} variant="contained" color="primary" disabled={uploading}>{isEditMode ? 'Update' : 'Add'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>Are you sure you want to delete this Court Case?</DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={uploading}>Cancel</Button>
          <Button onClick={handleDelete} variant="contained" color="error" disabled={uploading}>Delete</Button>
        </DialogActions>
      </Dialog>

      <SnackBarComponent open={snackbarOpen} message={snackbarMessage} severity={snackbarSeverity} onClose={handleSnackbarClose} />
    </Box>
  );
};
